import {
  X,
  Download,
  Trash2,
  Search,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Maximize2,
  Minimize2,
  GripHorizontal,
  AlertTriangle,
  Info,
  Terminal,
  Network,
  Copy,
  Check,
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useFilteredApiCalls,
  useFilteredConsoleEntries,
  ApiFilterType,
  ConsoleFilterType,
} from '@/hooks/useDebugFilters';
import { cn } from '@/lib/utils';
import { useApiDebugStore, ApiCall } from '@/stores/apiDebugStore';
import { useConsoleDebugStore, ConsoleEntry } from '@/stores/consoleDebugStore';

interface DebugPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PanelState {
  x: number;
  y: number;
  width: number;
  height: number;
  isMinimized: boolean;
}

const DEFAULT_PANEL_STATE: PanelState = {
  x: typeof window !== 'undefined' ? window.innerWidth - 550 : 550,
  y: 80,
  width: 540,
  height: 700,
  isMinimized: false,
};

export const DebugPanel: React.FC<DebugPanelProps> = ({ isOpen, onClose }) => {
  const { calls, clearCalls, exportCalls } = useApiDebugStore();
  const {
    entries: consoleEntries,
    clearEntries: clearConsole,
    exportEntries: exportConsole,
  } = useConsoleDebugStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [consoleSearchTerm, setConsoleSearchTerm] = useState('');
  const [expandedCalls, setExpandedCalls] = useState<Set<string>>(new Set());
  const [expandedConsole, setExpandedConsole] = useState<Set<string>>(new Set());
  const [selectedFilter, setSelectedFilter] = useState<ApiFilterType>('all');
  const [consoleFilter, setConsoleFilter] = useState<ConsoleFilterType>('all');
  const [activeTab, setActiveTab] = useState<'api' | 'console'>('api');
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());

  const panelRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ width: 0, height: 0, x: 0, y: 0 });

  const [panelState, setPanelState] = useState<PanelState>(() => {
    const saved = localStorage.getItem('debug-panel-state');
    if (saved) {
      try {
        const state = JSON.parse(saved);
        return {
          ...state,
          x: Math.min(Math.max(0, state.x), window.innerWidth - 200),
          y: Math.min(Math.max(0, state.y), window.innerHeight - 100),
        };
      } catch {
        return DEFAULT_PANEL_STATE;
      }
    }
    return DEFAULT_PANEL_STATE;
  });

  // Calculate dynamic content height based on panel size
  const getContentMaxHeight = () => {
    if (panelState.isMinimized) return 'auto';
    // Account for header, tabs, controls (approximately 200px with reduced spacing)
    const availableHeight = panelState.height - 200;
    return Math.max(200, availableHeight * 0.5); // 50% of available space, min 200px
  };

  // Count errors and warnings
  const errorCount = consoleEntries
    .filter((e) => e.level === 'error')
    .reduce((sum, e) => sum + e.count, 0);
  const warnCount = consoleEntries
    .filter((e) => e.level === 'warn')
    .reduce((sum, e) => sum + e.count, 0);

  useEffect(() => {
    localStorage.setItem('debug-panel-state', JSON.stringify(panelState));
  }, [panelState]);

  // Keyboard shortcuts and focus management
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key to close
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Focus management - focus the search input when opened
    if (panelRef.current && isOpen) {
      const searchInput = panelRef.current.querySelector('input[type="text"]') as HTMLInputElement;
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleResize = () => {
      setPanelState((prev) => ({
        ...prev,
        x: Math.min(prev.x, window.innerWidth - 200),
        y: Math.min(prev.y, window.innerHeight - 100),
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panelState.x,
        y: e.clientY - panelState.y,
      });
      e.preventDefault();
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    setResizeStart({
      width: panelState.width,
      height: panelState.height,
      x: e.clientX,
      y: e.clientY,
    });
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.min(
          Math.max(0, e.clientX - dragStart.x),
          window.innerWidth - panelState.width
        );
        const newY = Math.min(Math.max(0, e.clientY - dragStart.y), window.innerHeight - 100);
        setPanelState((prev) => ({ ...prev, x: newX, y: newY }));
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        const newWidth = Math.min(
          Math.max(350, resizeStart.width + deltaX),
          window.innerWidth - panelState.x
        );
        const newHeight = Math.min(
          Math.max(400, resizeStart.height + deltaY),
          window.innerHeight - panelState.y
        );
        setPanelState((prev) => ({ ...prev, width: newWidth, height: newHeight }));
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [
    isDragging,
    isResizing,
    dragStart,
    resizeStart,
    panelState.x,
    panelState.y,
    panelState.width,
  ]);

  const toggleMinimize = () => {
    setPanelState((prev) => ({ ...prev, isMinimized: !prev.isMinimized }));
  };

  // Use custom hooks for filtering
  const filteredCalls = useFilteredApiCalls(calls, searchTerm, selectedFilter);
  const filteredConsole = useFilteredConsoleEntries(
    consoleEntries,
    consoleSearchTerm,
    consoleFilter
  );

  const toggleExpanded = (callId: string) => {
    setExpandedCalls((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  const toggleConsoleExpanded = (entryId: string) => {
    setExpandedConsole((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const handleExport = () => {
    const data = activeTab === 'api' ? exportCalls() : exportConsole();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-debug-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (call: ApiCall) => {
    if (call.error) return 'text-red-500 bg-red-50';
    if (!call.status) return 'text-yellow-500 bg-yellow-50';
    if (call.status >= 200 && call.status < 300) return 'text-green-500 bg-green-50';
    if (call.status >= 400) return 'text-red-500 bg-red-50';
    return 'text-yellow-500 bg-yellow-50';
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'bg-blue-100 text-blue-700';
      case 'POST':
        return 'bg-green-100 text-green-700';
      case 'PUT':
        return 'bg-yellow-100 text-yellow-700';
      case 'PATCH':
        return 'bg-orange-100 text-orange-700';
      case 'DELETE':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getLogLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <AlertCircle className="h-3.5 w-3.5" />;
      case 'warn':
        return <AlertTriangle className="h-3.5 w-3.5" />;
      case 'info':
        return <Info className="h-3.5 w-3.5" />;
      default:
        return <Terminal className="h-3.5 w-3.5" />;
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'warn':
        return 'text-yellow-600 bg-yellow-50';
      case 'info':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return '-';
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString();
  };

  const handleCopy = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems((prev) => new Set(prev).add(itemId));
      setTimeout(() => {
        setCopiedItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const getErrorTypeName = (status?: number) => {
    if (!status) return 'Network Error';
    switch (status) {
      case 400:
        return 'Bad Request';
      case 401:
        return 'Unauthorized';
      case 403:
        return 'Forbidden';
      case 404:
        return 'Not Found';
      case 405:
        return 'Method Not Allowed';
      case 408:
        return 'Request Timeout';
      case 409:
        return 'Conflict';
      case 422:
        return 'Unprocessable Entity';
      case 429:
        return 'Too Many Requests';
      case 500:
        return 'Internal Server Error';
      case 502:
        return 'Bad Gateway';
      case 503:
        return 'Service Unavailable';
      case 504:
        return 'Gateway Timeout';
      default:
        if (status >= 400 && status < 500) return 'Client Error';
        if (status >= 500) return 'Server Error';
        return 'Unknown Error';
    }
  };

  const getErrorSource = (
    call: ApiCall
  ): {
    source: 'frontend' | 'backend' | 'network' | 'frontend/backend';
    description: string;
    recommendation: string;
  } => {
    // Network errors (no status code)
    if (!call.status) {
      if (call.error?.message?.includes('Network')) {
        return {
          source: 'network',
          description: 'Network connectivity issue - unable to reach the server',
          recommendation:
            'Check your internet connection, VPN settings, or if the server is running',
        };
      }
      if (call.error?.message?.includes('CORS')) {
        return {
          source: 'frontend',
          description: 'CORS policy blocking the request - frontend configuration issue',
          recommendation: 'Backend needs to allow your frontend origin in CORS settings',
        };
      }
      if (call.error?.message?.includes('timeout')) {
        return {
          source: 'network',
          description: 'Request timed out - server took too long to respond',
          recommendation:
            'Check if the server is overloaded or if there are network latency issues',
        };
      }
      return {
        source: 'network',
        description: 'Connection failed - unable to establish connection to server',
        recommendation: 'Verify the API endpoint URL and server availability',
      };
    }

    // 4xx errors - typically frontend/client issues
    if (call.status >= 400 && call.status < 500) {
      switch (call.status) {
        case 400:
          return {
            source: 'frontend',
            description: 'Frontend sent invalid data or malformed request',
            recommendation: 'Check the request payload format and required fields',
          };
        case 401:
          return {
            source: 'frontend',
            description: 'Authentication failed - missing or invalid credentials',
            recommendation: 'Check if token is expired or if login is required',
          };
        case 403:
          return {
            source: 'frontend',
            description: 'Authorization failed - user lacks required permissions',
            recommendation: 'Verify user has the correct role/permissions for this action',
          };
        case 404:
          return {
            source: 'frontend/backend',
            description:
              'Endpoint not found - Could be wrong URL (frontend) or unimplemented API (backend)',
            recommendation:
              'Check if: 1) Frontend is calling the correct URL, or 2) Backend API needs to be implemented',
          };
        case 422:
          return {
            source: 'frontend',
            description: 'Frontend sent data that failed validation rules',
            recommendation: 'Check the validation errors and ensure data meets requirements',
          };
        case 429:
          return {
            source: 'frontend',
            description: 'Frontend making too many requests - rate limited',
            recommendation: 'Implement request throttling or caching on the frontend',
          };
        default:
          return {
            source: 'frontend',
            description: 'Client-side error - issue with the request from frontend',
            recommendation: 'Review the request parameters and headers',
          };
      }
    }

    // 5xx errors - backend/server issues
    if (call.status >= 500) {
      switch (call.status) {
        case 500:
          return {
            source: 'backend',
            description: 'Backend server crashed or encountered an unhandled error',
            recommendation: 'Check server logs for the actual error and stack trace',
          };
        case 502:
          return {
            source: 'backend',
            description: 'Backend server or gateway configuration issue',
            recommendation: 'Check if the backend service is running and properly configured',
          };
        case 503:
          return {
            source: 'backend',
            description: 'Backend service temporarily unavailable or under maintenance',
            recommendation: 'Wait and retry, or check server status and deployment',
          };
        case 504:
          return {
            source: 'backend',
            description: 'Backend operation timed out - server processing took too long',
            recommendation: 'Backend may need optimization or the operation is too complex',
          };
        default:
          return {
            source: 'backend',
            description: 'Server-side error - backend failed to process the request',
            recommendation: 'Check backend logs and server health',
          };
      }
    }

    // Other status codes
    return {
      source: 'backend',
      description: 'Unexpected response from server',
      recommendation: 'Check both frontend request and backend response',
    };
  };

  const createErrorSummary = (call: ApiCall) => {
    const errorType = getErrorTypeName(call.status);
    const errorSource = getErrorSource(call);
    const timestamp = new Date(call.timestamp).toLocaleString();

    let summary = `🚨 API ERROR REPORT\n`;
    summary += `${'='.repeat(50)}\n\n`;

    // Error Source Analysis
    summary += `🔍 ERROR SOURCE: ${errorSource.source.toUpperCase()}\n`;
    summary += `📝 ${errorSource.description}\n`;
    summary += `💡 Recommendation: ${errorSource.recommendation}\n`;
    summary += `\n${'─'.repeat(50)}\n\n`;

    summary += `📍 URL: ${call.url}\n`;
    summary += `🔧 Method: ${call.method}\n`;
    summary += `❌ Status: ${call.status || 'N/A'} - ${errorType}\n`;
    summary += `⏱️ Timestamp: ${timestamp}\n`;
    summary += call.duration ? `⏳ Duration: ${formatDuration(call.duration)}\n` : '';
    summary += `\n`;

    if (call.error) {
      summary += `ERROR DETAILS:\n`;
      summary += `${'─'.repeat(30)}\n`;
      summary += `Message: ${call.error.message}\n`;
      if (call.error.code) summary += `Code: ${call.error.code}\n`;
      summary += `\n`;
    }

    if (call.requestData) {
      summary += `REQUEST DATA:\n`;
      summary += `${'─'.repeat(30)}\n`;
      summary += `${JSON.stringify(call.requestData, null, 2)}\n\n`;
    }

    if (call.responseData) {
      summary += `RESPONSE DATA:\n`;
      summary += `${'─'.repeat(30)}\n`;
      summary += `${JSON.stringify(call.responseData, null, 2)}\n\n`;
    }

    if (call.error?.stack) {
      summary += `STACK TRACE:\n`;
      summary += `${'─'.repeat(30)}\n`;
      summary += `${call.error.stack}\n`;
    }

    return summary;
  };

  const contentMaxHeight = getContentMaxHeight();

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Debug Panel"
      aria-describedby="debug-panel-description"
      className={cn(
        'fixed bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden',
        isDragging && 'cursor-move',
        'z-[9999]'
      )}
      style={{
        left: `${panelState.x}px`,
        top: `${panelState.y}px`,
        width: panelState.isMinimized ? '320px' : `${panelState.width}px`,
        height: panelState.isMinimized ? 'auto' : `${panelState.height}px`,
      }}
      onMouseDown={handleMouseDown}
    >
      <span id="debug-panel-description" className="sr-only">
        Debug panel showing API calls and console logs. Use Tab to navigate between controls.
      </span>
      {/* Header */}
      <div className="drag-handle flex items-center justify-between p-2 border-b cursor-move bg-gray-50 rounded-t-lg flex-shrink-0">
        <div className="flex items-center gap-2">
          <GripHorizontal className="h-4 w-4 text-gray-400" aria-hidden="true" />
          <h2 className="text-sm font-semibold select-none">Debug Panel</h2>
          <div className="flex gap-1">
            <Badge variant="secondary" className="text-xs">
              <Network className="h-3 w-3 mr-1" />
              {calls.length}
            </Badge>
            {errorCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                <AlertCircle className="h-3 w-3 mr-0.5" />
                {errorCount}
              </Badge>
            )}
            {warnCount > 0 && (
              <Badge className="text-xs bg-yellow-100 text-yellow-700">
                <AlertTriangle className="h-3 w-3 mr-0.5" />
                {warnCount}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMinimize}
            className="h-7 w-7"
            aria-label={panelState.isMinimized ? 'Maximize panel' : 'Minimize panel'}
          >
            {panelState.isMinimized ? (
              <Maximize2 className="h-3.5 w-3.5" />
            ) : (
              <Minimize2 className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-7 w-7"
            aria-label="Close debug panel"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {!panelState.isMinimized && (
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'api' | 'console')}
          className="flex-1 flex flex-col min-h-0 overflow-hidden"
        >
          <TabsList className="grid w-full grid-cols-2 rounded-none border-b flex-shrink-0">
            <TabsTrigger value="api" className="text-xs">
              <Network className="h-3.5 w-3.5 mr-1.5" />
              API Calls ({calls.length})
            </TabsTrigger>
            <TabsTrigger value="console" className="text-xs">
              <Terminal className="h-3.5 w-3.5 mr-1.5" />
              Console ({consoleEntries.length})
            </TabsTrigger>
          </TabsList>

          {/* API Calls Tab */}
          <TabsContent
            value="api"
            className="flex-1 flex flex-col m-0 p-0 min-h-0 overflow-hidden"
            forceMount={activeTab === 'api' ? undefined : { hidden: true }}
          >
            <div className="p-2 space-y-1.5 border-b bg-gray-50 flex-shrink-0">
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
                  aria-hidden="true"
                />
                <Input
                  placeholder="Search by URL, method, or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-8 text-sm"
                  aria-label="Search API calls"
                />
              </div>

              <div className="flex gap-1.5">
                <Button
                  variant={selectedFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedFilter('all')}
                  className="h-7 text-xs"
                >
                  All
                </Button>
                <Button
                  variant={selectedFilter === 'success' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedFilter('success')}
                  className="h-7 text-xs"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Success
                </Button>
                <Button
                  variant={selectedFilter === 'error' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedFilter('error')}
                  className="h-7 text-xs"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Errors
                </Button>
              </div>

              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={calls.length === 0}
                  className="h-7 text-xs"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearCalls}
                  disabled={calls.length === 0}
                  className="h-7 text-xs"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              <div className="px-2 py-2 space-y-1.5">
                {filteredCalls.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    {calls.length === 0
                      ? 'No API calls recorded yet'
                      : 'No calls match your filter'}
                  </div>
                ) : (
                  filteredCalls.map((call) => (
                    <div key={call.id} className="border rounded-md overflow-hidden bg-white">
                      <button
                        className="w-full p-2 flex items-start gap-2 hover:bg-gray-50 transition-colors text-left"
                        onClick={() => toggleExpanded(call.id)}
                      >
                        <div className="mt-0.5">
                          {expandedCalls.has(call.id) ? (
                            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <Badge
                              className={cn('text-xs px-1.5 py-0', getMethodColor(call.method))}
                            >
                              {call.method}
                            </Badge>
                            {call.status && (
                              <Badge className={cn('text-xs px-1.5 py-0', getStatusColor(call))}>
                                {call.status}
                              </Badge>
                            )}
                            {call.duration && (
                              <span className="text-xs text-gray-500 flex items-center gap-0.5">
                                <Clock className="h-3 w-3" />
                                {formatDuration(call.duration)}
                              </span>
                            )}
                            {/* Show error report copy button for failed requests */}
                            {(call.error || (call.status && call.status >= 400)) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 ml-auto"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const errorSummary = createErrorSummary(call);
                                  handleCopy(errorSummary, `error-summary-${call.id}`);
                                }}
                                title="Copy complete error report"
                              >
                                {copiedItems.has(`error-summary-${call.id}`) ? (
                                  <Check className="h-3 w-3 text-green-600" />
                                ) : (
                                  <Copy className="h-3 w-3 text-red-600" />
                                )}
                              </Button>
                            )}
                          </div>
                          <div className="text-xs text-gray-700 truncate pr-2" title={call.url}>
                            {call.url}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatTime(call.timestamp)}
                          </div>
                        </div>
                      </button>

                      {expandedCalls.has(call.id) && (
                        <div className="border-t bg-gray-50 p-2 space-y-2 text-xs overflow-hidden">
                          {/* Show error summary for failed requests */}
                          {(call.error || (call.status && call.status >= 400)) &&
                            (() => {
                              const errorSource = getErrorSource(call);
                              const isBackendError = errorSource.source === 'backend';
                              const isFrontendError = errorSource.source === 'frontend';
                              const isNetworkError = errorSource.source === 'network';
                              const isDualError = errorSource.source === 'frontend/backend';

                              return (
                                <div
                                  className={cn(
                                    'border rounded-md p-2 mb-2',
                                    isBackendError && 'bg-orange-50 border-orange-200',
                                    isFrontendError && 'bg-blue-50 border-blue-200',
                                    isNetworkError && 'bg-red-50 border-red-200',
                                    isDualError && 'bg-purple-50 border-purple-200'
                                  )}
                                >
                                  <div className="flex items-start justify-between mb-1">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle
                                        className={cn(
                                          'h-4 w-4 flex-shrink-0 mt-0.5',
                                          isBackendError && 'text-orange-600',
                                          isFrontendError && 'text-blue-600',
                                          isNetworkError && 'text-red-600',
                                          isDualError && 'text-purple-600'
                                        )}
                                      />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <div
                                            className={cn(
                                              'font-semibold',
                                              isBackendError && 'text-orange-900',
                                              isFrontendError && 'text-blue-900',
                                              isNetworkError && 'text-red-900',
                                              isDualError && 'text-purple-900'
                                            )}
                                          >
                                            {getErrorTypeName(call.status)} (
                                            {call.status || 'No Status'})
                                          </div>
                                          {isDualError ? (
                                            <div className="flex gap-1">
                                              <Badge className="text-xs bg-blue-200 text-blue-800">
                                                FRONTEND?
                                              </Badge>
                                              <Badge className="text-xs bg-orange-200 text-orange-800">
                                                BACKEND?
                                              </Badge>
                                            </div>
                                          ) : (
                                            <Badge
                                              className={cn(
                                                'text-xs',
                                                isBackendError && 'bg-orange-200 text-orange-800',
                                                isFrontendError && 'bg-blue-200 text-blue-800',
                                                isNetworkError && 'bg-red-200 text-red-800'
                                              )}
                                            >
                                              {errorSource.source.toUpperCase()} ERROR
                                            </Badge>
                                          )}
                                        </div>
                                        <div
                                          className={cn(
                                            'text-xs',
                                            isBackendError && 'text-orange-700',
                                            isFrontendError && 'text-blue-700',
                                            isNetworkError && 'text-red-700',
                                            isDualError && 'text-purple-700'
                                          )}
                                        >
                                          {errorSource.description}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1">
                                          <strong>💡 Fix:</strong> {errorSource.recommendation}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className={cn(
                                        'h-7 text-xs ml-2',
                                        isBackendError && 'border-orange-300 hover:bg-orange-100',
                                        isFrontendError && 'border-blue-300 hover:bg-blue-100',
                                        isNetworkError && 'border-red-300 hover:bg-red-100',
                                        isDualError && 'border-purple-300 hover:bg-purple-100'
                                      )}
                                      onClick={() => {
                                        const errorSummary = createErrorSummary(call);
                                        handleCopy(errorSummary, `full-error-${call.id}`);
                                      }}
                                    >
                                      {copiedItems.has(`full-error-${call.id}`) ? (
                                        <>
                                          <Check className="h-3 w-3 mr-1 text-green-600" />
                                          Copied!
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="h-3 w-3 mr-1" />
                                          Copy Report
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              );
                            })()}

                          <div>
                            <div className="flex items-center justify-between mb-0.5">
                              <div className="font-semibold text-gray-600">Full URL</div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleCopy(call.url, `url-${call.id}`)}
                                title="Copy URL"
                              >
                                {copiedItems.has(`url-${call.id}`) ? (
                                  <Check className="h-3 w-3 text-green-600" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                            <div className="text-gray-700 break-all bg-white p-1.5 rounded border overflow-x-auto max-w-full">
                              {call.url}
                            </div>
                          </div>

                          {call.requestData && (
                            <div>
                              <div className="font-semibold text-gray-600 mb-0.5">Request Body</div>
                              <pre
                                className="text-gray-700 bg-white p-1.5 rounded border overflow-auto text-xs"
                                style={{ maxHeight: `${contentMaxHeight}px` }}
                              >
                                {JSON.stringify(call.requestData, null, 2)}
                              </pre>
                            </div>
                          )}

                          {call.responseData && (
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <div className="font-semibold text-gray-600">Response Data</div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() =>
                                    handleCopy(
                                      JSON.stringify(call.responseData, null, 2),
                                      `response-${call.id}`
                                    )
                                  }
                                  title="Copy response data"
                                >
                                  {copiedItems.has(`response-${call.id}`) ? (
                                    <Check className="h-3 w-3 text-green-600" />
                                  ) : (
                                    <Copy className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                              <pre
                                className="text-gray-700 bg-white p-1.5 rounded border overflow-auto text-xs"
                                style={{ maxHeight: `${contentMaxHeight}px` }}
                              >
                                {JSON.stringify(call.responseData, null, 2)}
                              </pre>
                            </div>
                          )}

                          {call.error && (
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <div className="font-semibold text-red-600">Error Details</div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => {
                                    const errorText = `Error: ${call.error.message}${call.error.code ? `\nCode: ${call.error.code}` : ''}${call.error.stack ? `\nStack: ${call.error.stack}` : ''}`;
                                    handleCopy(errorText, `error-${call.id}`);
                                  }}
                                  title="Copy error details"
                                >
                                  {copiedItems.has(`error-${call.id}`) ? (
                                    <Check className="h-3 w-3 text-green-600" />
                                  ) : (
                                    <Copy className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                              <div
                                className="text-red-700 bg-red-50 p-1.5 rounded border border-red-200 overflow-auto"
                                style={{ maxHeight: `${contentMaxHeight}px` }}
                              >
                                <div className="font-semibold break-words">
                                  {call.error.message}
                                </div>
                                {call.error.code && (
                                  <div className="mt-0.5">Code: {call.error.code}</div>
                                )}
                                {call.error.stack && (
                                  <pre className="mt-1 text-xs whitespace-pre-wrap break-words">
                                    {call.error.stack}
                                  </pre>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Console Tab */}
          <TabsContent
            value="console"
            className="flex-1 flex flex-col m-0 p-0 min-h-0 overflow-hidden"
            forceMount={activeTab === 'console' ? undefined : { hidden: true }}
          >
            <div className="p-2 space-y-1.5 border-b bg-gray-50 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Search console messages..."
                  value={consoleSearchTerm}
                  onChange={(e) => setConsoleSearchTerm(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>

              <div className="flex gap-1.5">
                <Button
                  variant={consoleFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setConsoleFilter('all')}
                  className="h-7 text-xs"
                >
                  All
                </Button>
                <Button
                  variant={consoleFilter === 'error' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setConsoleFilter('error')}
                  className="h-7 text-xs"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Errors
                </Button>
                <Button
                  variant={consoleFilter === 'warn' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setConsoleFilter('warn')}
                  className="h-7 text-xs"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Warnings
                </Button>
              </div>

              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={consoleEntries.length === 0}
                  className="h-7 text-xs"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearConsole}
                  disabled={consoleEntries.length === 0}
                  className="h-7 text-xs"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              <div className="px-2 py-2 space-y-1.5">
                {filteredConsole.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    {consoleEntries.length === 0
                      ? 'No console messages yet'
                      : 'No messages match your filter'}
                  </div>
                ) : (
                  filteredConsole.map((entry) => (
                    <div key={entry.id} className="border rounded-md overflow-hidden bg-white">
                      <button
                        className="w-full p-2 flex items-start gap-2 hover:bg-gray-50 transition-colors text-left"
                        onClick={() => toggleConsoleExpanded(entry.id)}
                      >
                        <div className="mt-0.5">
                          {expandedConsole.has(entry.id) ? (
                            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <Badge
                              className={cn(
                                'text-xs px-1.5 py-0 flex items-center gap-1',
                                getLogLevelColor(entry.level)
                              )}
                            >
                              {getLogLevelIcon(entry.level)}
                              {entry.level}
                            </Badge>
                            {entry.count > 1 && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                ×{entry.count}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-gray-700 break-words pr-2 line-clamp-3">
                            {entry.message}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>{formatTime(entry.timestamp)}</span>
                            {entry.source && (
                              <span className="text-blue-600 truncate max-w-[200px]">
                                {entry.source}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>

                      {expandedConsole.has(entry.id) && (
                        <div className="border-t bg-gray-50 p-2 space-y-2 text-xs overflow-hidden">
                          <div>
                            <div className="flex items-center justify-between mb-0.5">
                              <div className="font-semibold text-gray-600">Full Message</div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  const fullText = `${entry.message}${entry.stack ? `\n\nStack Trace:\n${entry.stack}` : ''}${entry.source ? `\n\nSource: ${entry.source}` : ''}`;
                                  handleCopy(fullText, `console-${entry.id}`);
                                }}
                                title="Copy message details"
                              >
                                {copiedItems.has(`console-${entry.id}`) ? (
                                  <Check className="h-3 w-3 text-green-600" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                            <div
                              className="text-gray-700 break-words bg-white p-1.5 rounded border whitespace-pre-wrap overflow-auto"
                              style={{ maxHeight: `${contentMaxHeight}px` }}
                            >
                              {entry.message}
                            </div>
                          </div>

                          {entry.stack && (
                            <div>
                              <div className="font-semibold text-gray-600 mb-0.5">Stack Trace</div>
                              <pre
                                className="text-gray-700 bg-white p-1.5 rounded border overflow-auto text-xs"
                                style={{ maxHeight: `${contentMaxHeight}px` }}
                              >
                                {entry.stack}
                              </pre>
                            </div>
                          )}

                          {entry.details && entry.details.length > 0 && (
                            <div>
                              <div className="font-semibold text-gray-600 mb-0.5">
                                Additional Details
                              </div>
                              <pre
                                className="text-gray-700 bg-white p-1.5 rounded border overflow-auto text-xs"
                                style={{ maxHeight: `${contentMaxHeight}px` }}
                              >
                                {JSON.stringify(entry.details, null, 2)}
                              </pre>
                            </div>
                          )}

                          {entry.source && (
                            <div>
                              <div className="font-semibold text-gray-600 mb-0.5">Source</div>
                              <div className="text-blue-600 bg-white p-1.5 rounded border break-all overflow-x-auto">
                                {entry.source}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Resize handle */}
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-10"
            onMouseDown={handleResizeMouseDown}
          >
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 16 16">
              <path d="M 12 12 L 16 16 L 16 12 Z" />
              <path d="M 8 12 L 12 16 L 12 12 Z" opacity="0.5" />
              <path d="M 4 12 L 8 16 L 8 12 Z" opacity="0.3" />
            </svg>
          </div>
        </Tabs>
      )}
    </div>
  );
};
